/**
 * Admin: Schemalagd AI-generering
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { aiScheduleService } from '../services/aiScheduleService';
import { categoryService } from '../services/categoryService';
import { audienceService } from '../services/audienceService';

const DEFAULT_FORM = {
  name: '',
  isActive: true,
  timezone: 'Europe/Stockholm',
  times: [],
  daysOfWeek: [],
  config: {
    amount: 10,
    difficulty: 'medium',
    provider: 'random',
    category: '',
    ageGroup: '',
    targetAudience: '',
    generateIllustrations: false
  }
};

const weekdayOptions = [
  { value: 1, label: 'Mån' },
  { value: 2, label: 'Tis' },
  { value: 3, label: 'Ons' },
  { value: 4, label: 'Tor' },
  { value: 5, label: 'Fre' },
  { value: 6, label: 'Lör' },
  { value: 0, label: 'Sön' }
];

const difficultyOptions = [
  { value: 'easy', label: 'Lätt' },
  { value: 'medium', label: 'Medel' },
  { value: 'hard', label: 'Svår' }
];

const normalizeTimeInput = (value) => {
  if (!value) return null;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

const AdminAISchedulesPage = () => {
  const navigate = useNavigate();
  const { isSuperUser, currentUser } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [editingId, setEditingId] = useState(null);
  const [timeInput, setTimeInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [targetAudiences, setTargetAudiences] = useState([]);
  const [providers, setProviders] = useState([]);

  const loadSchedules = useCallback(async () => {
    if (!isSuperUser) return;
    setLoading(true);
    setError('');
    try {
      const data = await aiScheduleService.getSchedules({ userEmail: currentUser?.email || '' });
      setSchedules(data);
    } catch (err) {
      setError(err.message || 'Kunde inte hämta scheman.');
    } finally {
      setLoading(false);
    }
  }, [currentUser?.email, isSuperUser]);

  const loadMetadata = useCallback(async () => {
    if (!isSuperUser) return;
    try {
      const [cats, audienceConfig] = await Promise.all([
        categoryService.getCategories({ includeInactive: true, userEmail: currentUser?.email || '' }),
        audienceService.getAudienceConfig({ includeInactive: true, userEmail: currentUser?.email || '' })
      ]);
      setCategories(cats || []);
      setAgeGroups(audienceConfig?.ageGroups || []);
      setTargetAudiences(audienceConfig?.targetAudiences || []);
    } catch (err) {
      setError(err.message || 'Kunde inte hämta metadata.');
    }
  }, [currentUser?.email, isSuperUser]);

  const loadProviders = useCallback(async () => {
    if (!isSuperUser) return;
    try {
      const response = await fetch('/api/getProviderSettings', {
        headers: {
          'x-user-email': currentUser?.email || ''
        }
      });
      const data = await response.json();
      if (!response.ok || !data.settings) {
        throw new Error(data.error || 'Kunde inte hämta providerinställningar');
      }
      const purposeMap = data.settings?.purposes?.generation || {};
      const providersRaw = Object.entries(data.settings.providers || {})
        .map(([id, provider]) => ({
          id,
          label: provider.displayName || id,
          hasKey: provider.hasKey !== false,
          enabled: purposeMap[id] !== false
        }))
        .filter((provider) => provider.enabled && provider.hasKey);
      setProviders(providersRaw);
    } catch (err) {
      setError(err.message || 'Kunde inte hämta providerinställningar.');
    }
  }, [currentUser?.email, isSuperUser]);

  useEffect(() => {
    if (!isSuperUser) {
      navigate('/');
      return;
    }
    loadSchedules();
    loadMetadata();
    loadProviders();
  }, [isSuperUser, navigate, loadSchedules, loadMetadata, loadProviders]);

  const providerOptions = useMemo(() => ([
    { id: 'random', label: 'Slumpmässig (aktiva)' },
    ...providers
  ]), [providers]);

  const handleAddTime = () => {
    const normalized = normalizeTimeInput(timeInput);
    if (!normalized) {
      setError('Ange tid som HH:MM.');
      return;
    }
    setForm((prev) => ({
      ...prev,
      times: Array.from(new Set([...(prev.times || []), normalized])).sort()
    }));
    setTimeInput('');
  };

  const handleRemoveTime = (value) => {
    setForm((prev) => ({
      ...prev,
      times: (prev.times || []).filter((time) => time !== value)
    }));
  };

  const toggleDay = (value) => {
    setForm((prev) => {
      const exists = (prev.daysOfWeek || []).includes(value);
      return {
        ...prev,
        daysOfWeek: exists
          ? prev.daysOfWeek.filter((day) => day !== value)
          : [...prev.daysOfWeek, value].sort()
      };
    });
  };

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setEditingId(null);
    setTimeInput('');
  };

  const handleEdit = (schedule) => {
    setEditingId(schedule.id);
    setForm({
      name: schedule.name || '',
      isActive: schedule.isActive !== false,
      timezone: schedule.timezone || 'Europe/Stockholm',
      times: schedule.times || [],
      daysOfWeek: schedule.daysOfWeek || [],
      config: {
        ...DEFAULT_FORM.config,
        ...(schedule.config || {})
      }
    });
  };

  const handleSave = async () => {
    setError('');
    setStatus('');
    try {
      const payload = {
        name: form.name,
        isActive: form.isActive,
        timezone: form.timezone,
        times: form.times,
        daysOfWeek: form.daysOfWeek,
        config: form.config
      };
      if (editingId) {
        await aiScheduleService.updateSchedule({
          id: editingId,
          schedule: payload,
          userEmail: currentUser?.email || ''
        });
        setStatus('Schemat uppdaterades.');
      } else {
        await aiScheduleService.createSchedule({
          schedule: payload,
          userEmail: currentUser?.email || ''
        });
        setStatus('Schemat skapades.');
      }
      resetForm();
      loadSchedules();
    } catch (err) {
      setError(err.message || 'Kunde inte spara schemat.');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Vill du verkligen ta bort schemat?')) return;
    setError('');
    setStatus('');
    try {
      await aiScheduleService.deleteSchedule({ id: scheduleId, userEmail: currentUser?.email || '' });
      setStatus('Schemat togs bort.');
      loadSchedules();
    } catch (err) {
      setError(err.message || 'Kunde inte ta bort schemat.');
    }
  };

  const handleRunNow = async (scheduleId) => {
    setError('');
    setStatus('');
    try {
      await aiScheduleService.runSchedules({ scheduleId, userEmail: currentUser?.email || '' });
      setStatus('Schemat startades.');
      loadSchedules();
    } catch (err) {
      setError(err.message || 'Kunde inte starta schemat.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header title="AI-scheman" />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-10 space-y-6">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
          <h2 className="text-lg font-semibold text-white">Schemalagd AI-generering</h2>
          <p className="text-sm text-slate-300">
            Ange ett eller flera klockslag (HH:MM). Schemat startar genereringar och
            valideringen körs automatiskt efteråt.
          </p>
          <p className="text-xs text-slate-400">
            Körs via endpoint: <span className="text-slate-200">/api/ai-schedules/run</span>
          </p>
          <p className="text-xs text-slate-400">
            Tips: För Cloudflare Cron Trigger behöver du en liten Worker som POST:ar till
            <code className="ml-1">/api/ai-schedules/run</code>.
          </p>
        </div>

        <details className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-xs text-slate-300">
          <summary className="cursor-pointer text-sm font-semibold text-slate-100">
            Snabb Cron‑hjälp
          </summary>
          <div className="mt-3 space-y-2 text-slate-300">
            <p>1. Skapa en Cloudflare Worker med Cron Trigger (t.ex. varje minut eller var 5–10 min).</p>
            <p>
              2. Låt Workern göra en POST till{' '}
              <code>https://din-domän.se/api/ai-schedules/run</code>.
            </p>
            <p>
              3. Om <code>INTERNAL_TASK_SECRET</code> är satt: skicka header{' '}
              <code>x-task-secret</code>.
            </p>
            <p className="text-slate-400">
              Exempel: <code>fetch(url, {`{`} method: 'POST', headers: {'{'}'x-task-secret': '...'{'}'} {`}`})</code>
            </p>
          </div>
        </details>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-100">
              {editingId ? 'Redigera schema' : 'Skapa nytt schema'}
            </h3>
            {editingId && (
              <button
                className="text-xs text-slate-300 hover:text-white"
                onClick={resetForm}
              >
                Avbryt
              </button>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold text-slate-300">Namn</label>
              <input
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="Ex: Morgon + kväll"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300">Tidszon</label>
              <input
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.timezone}
                onChange={(event) => setForm((prev) => ({ ...prev, timezone: event.target.value }))}
                placeholder="Europe/Stockholm"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-xs font-semibold text-slate-300">Klockslag</label>
              <div className="mt-1 flex gap-2">
                <input
                  className="w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                  value={timeInput}
                  onChange={(event) => setTimeInput(event.target.value)}
                  placeholder="08:30"
                />
                <button
                  className="rounded bg-slate-700 px-3 text-sm"
                  onClick={handleAddTime}
                >
                  Lägg till
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(form.times || []).map((time) => (
                  <span key={time} className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-1 text-xs">
                    {time}
                    <button
                      className="text-slate-400 hover:text-white"
                      onClick={() => handleRemoveTime(time)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300">Dagar</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {weekdayOptions.map((day) => (
                  <button
                    key={day.value}
                    className={`px-3 py-1 rounded-full text-xs border ${
                      form.daysOfWeek.includes(day.value)
                        ? 'border-cyan-400 text-cyan-100'
                        : 'border-slate-700 text-slate-300'
                    }`}
                    onClick={() => toggleDay(day.value)}
                  >
                    {day.label}
                  </button>
                ))}
                <button
                  className="px-3 py-1 rounded-full text-xs border border-slate-700 text-slate-300"
                  onClick={() => setForm((prev) => ({ ...prev, daysOfWeek: [] }))}
                >
                  Alla dagar
                </button>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300">Antal frågor</label>
              <input
                type="number"
                min="1"
                max="50"
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.config.amount}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  config: { ...prev.config, amount: Number(event.target.value) }
                }))}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300">Svårighetsgrad</label>
              <select
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.config.difficulty}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  config: { ...prev.config, difficulty: event.target.value }
                }))}
              >
                {difficultyOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300">Provider</label>
              <select
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.config.provider}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  config: { ...prev.config, provider: event.target.value }
                }))}
              >
                {providerOptions.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300">Kategori</label>
              <select
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.config.category || ''}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  config: { ...prev.config, category: event.target.value || null }
                }))}
              >
                <option value="">(Ingen)</option>
                {categories.map((category) => (
                  <option key={category.name} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300">Åldersgrupp</label>
              <select
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.config.ageGroup || ''}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  config: { ...prev.config, ageGroup: event.target.value || null }
                }))}
              >
                <option value="">(Ingen)</option>
                {ageGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.label || group.id}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-300">Målgrupp</label>
              <select
                className="mt-1 w-full rounded bg-slate-800 border border-slate-700 px-3 py-2 text-sm"
                value={form.config.targetAudience || ''}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  config: { ...prev.config, targetAudience: event.target.value || null }
                }))}
              >
                <option value="">(Ingen)</option>
                {targetAudiences.map((audience) => (
                  <option key={audience.id} value={audience.id}>
                    {audience.label || audience.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(event) => setForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
              Aktivt schema
            </label>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              className="rounded bg-cyan-500 px-4 py-2 text-sm font-semibold text-black"
              onClick={handleSave}
              disabled={loading}
            >
              {editingId ? 'Uppdatera' : 'Skapa schema'}
            </button>
            <button
              className="rounded bg-slate-700 px-4 py-2 text-sm text-slate-100"
              onClick={loadSchedules}
              disabled={loading}
            >
              Uppdatera lista
            </button>
          </div>
          {status && <div className="text-xs text-emerald-300">{status}</div>}
          {error && <div className="text-xs text-rose-300">{error}</div>}
        </div>

        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-slate-200">Aktiva scheman</h3>
          {schedules.length === 0 && (
            <div className="text-sm text-slate-400">Inga scheman skapade ännu.</div>
          )}
          {schedules.map((schedule) => (
            <div key={schedule.id} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold text-white">{schedule.name}</div>
                  <div className="text-xs text-slate-400">
                    {schedule.times?.join(', ') || 'Inga tider'} · {schedule.timezone || 'UTC'}
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    className="rounded bg-slate-800 px-3 py-1 text-xs"
                    onClick={() => handleEdit(schedule)}
                  >
                    Redigera
                  </button>
                  <button
                    className="rounded bg-slate-800 px-3 py-1 text-xs"
                    onClick={() => handleRunNow(schedule.id)}
                  >
                    Kör nu
                  </button>
                  <button
                    className="rounded bg-rose-700/80 px-3 py-1 text-xs"
                    onClick={() => handleDelete(schedule.id)}
                  >
                    Ta bort
                  </button>
                </div>
              </div>
              <div className="text-xs text-slate-400">
                Nästa körning: {schedule.nextRunKey || 'Okänd'}
              </div>
              {schedule.latestRun && (
                <div className="text-xs text-slate-300">
                  Senast: {schedule.latestRun.runKey} · {schedule.latestRun.taskStatus || schedule.latestRun.status}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminAISchedulesPage;
